import React, { useCallback, useRef, useState } from "react";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faSmile } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { beforeWrite, type ModifierArguments, type Options } from "@popperjs/core";
import Tippy, { type TippyProps } from "@tippyjs/react/headless";
import { Emoji, Picker as EmojiPicker } from "emoji-mart";
import maxSize from "popper-max-size-modifier";

import { Button } from "~/ui";
import { HelpCenterIcon } from "~/ui/legacyFellowIndex";
import Modal from "~/ui/Modal";

import type { HomePanelGroup_group$data as HomePanelGroup } from "~/__generated__/HomePanelGroup_group.graphql";

import styles from "./css/CreateEditGroupModal.module.css";

export type ModalData = {
	emoji: string | null;
	name: string;
};

interface ModalProps {
	onClose: (result?: ModalData) => void;
	/** If provided, the existing group we're editing, rather than creating a new one from scratch */
	initialGroup?: HomePanelGroup;
}

// This makes the content of the tooltip not mount until the tooltip is visible which allows the emoji picker sections to work as expected.
// Without this the measured scroll positions for each emoji picker section are 0, so the top sections don't work unless you search first
// https://gist.github.com/atomiks/520f4b0c7b537202a23a3059d4eec908
export const LazyTippy = React.forwardRef<Element, TippyProps>(function LazyTippy(props, ref) {
	const [mounted, setMounted] = useState(false);

	const lazyPlugin = {
		fn: () => ({
			onMount: () => setMounted(true),
			onHidden: () => setMounted(false),
		}),
	};

	const computedProps = { ...props };

	computedProps.plugins = [lazyPlugin, ...(props.plugins || [])];

	if (props.render) {
		computedProps.render = (...args) => (mounted ? props.render?.(...args) : "");
	} else {
		computedProps.content = mounted ? props.content : "";
	}

	return <Tippy {...computedProps} ref={ref} />;
});

export default function CreateEditGroupModal(props: ModalProps) {
	const { onClose, initialGroup } = props;

	const [name, setName] = useState(initialGroup?.name || "");
	const [emoji, setEmoji] = useState(initialGroup?.emojiName || null);
	const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
	const textInput = useRef<HTMLInputElement>(null);

	// disable create if the following conditions are NOT met:
	const createDisabled = !(
		// The name is non-empty, and
		(
			name.length > 0 &&
			// If we had an `initialGroup`, i.e. we're editing, not creating, at least one field must have changed
			(initialGroup ? emoji != initialGroup.emojiName || name != initialGroup.name : true)
		)
	);

	const GROUP_NAME_MAX_LEN = 255;

	const modalTitle = initialGroup ? "Rename group" : "Create new group";
	const buttonTitle = initialGroup ? "Rename" : "Create";

	const cancel = useCallback(() => {
		onClose();
	}, [onClose]);

	const create = useCallback(() => {
		onClose({
			name: name,
			emoji: emoji,
		});
	}, [onClose, name, emoji]);

	const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setName(event.target.value);
	}, []);

	const selectEmoji = useCallback(emoji => {
		setEmoji(emoji.colons);
		setEmojiPickerOpen(false);
	}, []);

	const renderEmojiPicker = useCallback(() => {
		return (
			<EmojiPicker
				autoFocus
				emojiTooltip
				native
				emoji=""
				style={{ maxWidth: "95vw" }}
				// onClick={selectEmoji}
				onSelect={selectEmoji}
				skinEmoji="+1"
				title=""
				darkMode={document.documentElement.dataset.theme === "theme-dark"}
			/>
		);
	}, [selectEmoji]);

	const clearInput = useCallback(() => {
		setEmoji(null);
		setName("");
		textInput.current?.focus();
	}, []);

	const toggleEmojiPicker = useCallback(() => {
		setEmojiPickerOpen(!emojiPickerOpen);
	}, [emojiPickerOpen]);

	// this is needed so the emoji picker doesn't overflow the page and get cut off
	// https://www.npmjs.com/package/popper-max-size-modifier
	const popperModifiers = {
		modifiers: [
			maxSize,
			{
				name: "applyMaxSize",
				enabled: true,
				phase: beforeWrite,
				requires: ["maxSize"],
				fn({ state }: ModifierArguments<Options>) {
					const { width, height } = state.modifiersData.maxSize;

					state.styles.popper = {
						...state.styles.popper,
						maxWidth: `${width}px`,
						maxHeight: `${height}px`,
					};
				},
			},
		],
	};

	return (
		<Modal onClose={cancel}>
			<Modal.Header>
				<Modal.Title>{modalTitle}</Modal.Title>
				<Modal.HeaderActions onClose={cancel}>
					<HelpCenterIcon articleId="6345661" title="Customize your home panel" />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body>
				<div className={styles.body}>
					<div className={styles.subHeader}>
						<span className={styles.title}>Group name</span>
						<span className={styles.charCount}>
							{name.length}/{GROUP_NAME_MAX_LEN}
						</span>
					</div>
					<div className={styles.input}>
						<LazyTippy
							visible={emojiPickerOpen}
							onClickOutside={toggleEmojiPicker}
							interactive
							trigger="click"
							render={renderEmojiPicker}
							popperOptions={popperModifiers}
						>
							<button className={styles.emojiInput} onClick={toggleEmojiPicker}>
								<span className={styles.emoji}>
									{emoji ? <Emoji size={14} emoji={emoji} native /> : <FontAwesomeIcon icon={faSmile} />}
								</span>
								<span className={styles.caret}>
									<FontAwesomeIcon icon={faCaretDown} />
								</span>
							</button>
						</LazyTippy>
						<input
							className={styles.textInput}
							value={name}
							ref={textInput}
							maxLength={GROUP_NAME_MAX_LEN}
							type="text"
							placeholder="Eg. Projects"
							onChange={onChange}
							autoFocus
						/>
						<button className={styles.clearInput} onClick={clearInput}>
							<FontAwesomeIcon icon={faTimes} />
						</button>
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" size="normal" onClick={cancel}>
					Cancel
				</Button>
				<Button variant="primary" disabled={createDisabled} size="normal" onClick={create}>
					{buttonTitle}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
